import React, { useEffect, useRef, forwardRef, RefObject } from 'react';
import { Loader } from '../Loader';

type RefProp<T> = RefObject<T | null> | null;

const WINDOW_ROOT_MARGIN = '0px 0px 100% 0px';
const PARENT_ROOT_MARGIN = '0px 0px 150px 0px';

interface EndlessScrollProps {
  items: Readonly<{ length: number }>;
  loading: boolean;
  onLoadMore: () => void;
  isStopLoad: boolean;
  forwardedRef: RefProp<HTMLDivElement>;
  scrollContainer?: 'window' | 'parent';
}

const EndlessScroll: React.FC<
  EndlessScrollProps & {
    Component: React.ComponentType<{ forwardedRef: RefProp<HTMLDivElement> }>;
  }
> = ({
  onLoadMore,
  loading,
  isStopLoad,
  forwardedRef,
  scrollContainer = 'window',
  items,
  Component,
  ...props
}) => {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const onLoadMoreRef = useRef(onLoadMore);
  onLoadMoreRef.current = onLoadMore;

  useEffect(() => {
    if (loading || isStopLoad) return;

    const sentinel = sentinelRef.current;
    const root = scrollContainer === 'parent' ? forwardedRef?.current : null;
    if (!sentinel || (scrollContainer === 'parent' && !root)) return;

    const rootMargin =
      scrollContainer === 'parent' ? PARENT_ROOT_MARGIN : WINDOW_ROOT_MARGIN;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onLoadMoreRef.current();
        }
      },
      { root, rootMargin }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [loading, isStopLoad, scrollContainer, forwardedRef]);

  return (
    <>
      <Component {...props} forwardedRef={forwardedRef} />
      <div ref={sentinelRef} />
      <Loader loading={loading && !!items.length && !isStopLoad} endless />
    </>
  );
};

export const withEndlessScroll = <P extends object>(
  Component: React.ComponentType<
    P & { forwardedRef: RefObject<HTMLDivElement> }
  >
) => {
  const ForwardedComponent = forwardRef<
    HTMLDivElement,
    P & Omit<EndlessScrollProps, 'forwardedRef'>
  >((props, ref) => (
    <EndlessScroll
      {...props}
      forwardedRef={typeof ref !== 'function' ? ref : null}
      Component={
        Component as React.ComponentType<{
          forwardedRef: RefProp<HTMLDivElement>;
        }>
      }
    />
  ));

  const name = Component.displayName || Component.name;
  ForwardedComponent.displayName = `withEndlessScroll(${name})`;

  return ForwardedComponent;
};
